import { ArrowLeft, Moon, RotateCcw, Sun } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkline } from '@/components/charts/Sparkline';
import {
  Badge,
  Button,
  EntityPeek,
  HoldToConfirm,
  IconButton,
  LiveValue,
  Stat,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
} from '@/components/ui';
import { useTheme } from '@/theme/useTheme';
import { Demo } from './Demo';
import { PartTwo } from './PartTwo';

/*
  /design/character — «Характер»: приёмы, которые повторяются в каждом элементе и поэтому
  читаются как характер системы, а не как украшение одной страницы.
  Каждый приём здесь живой: его можно потрогать, а не прочитать описание.
  Принятые приёмы переезжают в frontend/DESIGN.md (R25–R27) и в примитивы.
*/

/** Значение, которое само шевелится: имитация живого опроса. */
function useDrift(base: number, spread: number, live: boolean, periodMs: number) {
  const [v, setV] = useState(base);
  useEffect(() => {
    if (!live) return;
    const id = window.setInterval(() => {
      setV((prev) => {
        const next = prev + (Math.random() - 0.5) * spread;
        return Math.max(base - spread * 2, Math.min(base + spread * 2, next));
      });
    }, periodMs);
    return () => window.clearInterval(id);
  }, [base, spread, live, periodMs]);
  return v;
}

function FreshnessDemo() {
  const [live, setLive] = useState(true);
  const [age, setAge] = useState(0);
  const period = 4000;

  const cpu = useDrift(34, 6, live, period);
  const la = useDrift(1.4, 0.5, live, period);
  const net = useDrift(1.2, 0.4, live, period);

  useEffect(() => {
    if (live) return;
    const id = window.setInterval(() => setAge((a) => a + 1), 1000);
    return () => window.clearInterval(id);
  }, [live]);

  const ageLabel = age < 60 ? `${age} с назад` : `${Math.floor(age / 60)} мин назад`;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-x-10 gap-y-5">
        <div>
          <div className="text-xs font-medium text-fg-muted">Процессор</div>
          <LiveValue value={cpu.toFixed(1)} unit="%" live={live} intervalMs={period} ageLabel={ageLabel} />
        </div>
        <div>
          <div className="text-xs font-medium text-fg-muted">Load average</div>
          <LiveValue value={la.toFixed(2)} live={live} intervalMs={period} ageLabel={ageLabel} />
        </div>
        <div>
          <div className="text-xs font-medium text-fg-muted">Сеть</div>
          <LiveValue
            value={net.toFixed(2)}
            unit="Гбит/с"
            live={live}
            intervalMs={period}
            ageLabel={ageLabel}
          />
        </div>
      </div>
      <div className="flex items-center gap-3 border-t border-line pt-4">
        <Switch
          size="sm"
          checked={live}
          onCheckedChange={(v) => {
            setLive(v);
            if (v) setAge(0);
          }}
          label="Поток данных"
        />
        <span className="text-xs text-fg-subtle">
          {live
            ? 'Кольцо добирается до следующего опроса, новое значение подкатывается снизу.'
            : 'Поток остановлен: значения выцвели и показывают возраст.'}
        </span>
      </div>
    </div>
  );
}

const cpuTrend = [22, 24, 23, 27, 31, 29, 33, 58, 74, 52, 39, 34];
const memTrend = [58, 58, 59, 59, 60, 60, 61, 61, 62, 61, 61, 61];
const diskTrend = [71, 71, 72, 72, 73, 73, 74, 74, 75, 76, 76, 77];

function HistoryDemo() {
  return (
    <div className="grid gap-5 md:grid-cols-2">
      <div>
        <div className="mb-3 text-2xs font-semibold text-fg-subtle uppercase">Как обычно</div>
        <div className="grid gap-3">
          <Stat label="Процессор" value="34" unit="%" trend={cpuTrend} delta={{ value: 6 }} />
        </div>
      </div>
      <div>
        <div className="mb-3 text-2xs font-semibold text-fg-subtle uppercase">С историей внутри</div>
        <div className="grid gap-3">
          <Stat
            label="Процессор"
            value="34"
            unit="%"
            trend={cpuTrend}
            trendFill="background"
            trendTone="brand"
            delta={{ value: 6 }}
            footer={<span className="text-fg-subtle">за 6 ч</span>}
          />
        </div>
      </div>
      <div className="grid gap-3 md:col-span-2 md:grid-cols-2">
        <Stat
          label="Память"
          value="61"
          unit="%"
          trend={memTrend}
          trendFill="background"
          delta={{ value: 1, label: 'ровно' }}
        />
        <Stat
          label="Диск"
          value="77"
          unit="%"
          trend={diskTrend}
          trendFill="background"
          trendTone="danger"
          delta={{ value: 6, goodDirection: 'down', label: 'растёт третий день' }}
        />
      </div>
    </div>
  );
}

const rows = [
  { id: 'host-338', cpu: 34, cpuTrend, mem: 61, incidents: 2, state: 'Проблема' as const },
  {
    id: 'host-341',
    cpu: 18,
    cpuTrend: [14, 15, 16, 15, 17, 16, 18, 17, 19, 18, 18, 18],
    mem: 44,
    incidents: 0,
    state: 'Норма' as const,
  },
  {
    id: 'host-354',
    cpu: 62,
    cpuTrend: [30, 33, 36, 40, 44, 47, 51, 54, 57, 59, 61, 62],
    mem: 73,
    incidents: 1,
    state: 'Норма' as const,
  },
  {
    id: 'host-011',
    cpu: 71,
    cpuTrend: [68, 70, 69, 72, 71, 73, 70, 72, 71, 70, 71, 71],
    mem: 81,
    incidents: 4,
    state: 'Проблема' as const,
  },
  {
    id: 'host-006',
    cpu: 11,
    cpuTrend: [9, 10, 11, 10, 12, 11, 10, 11, 12, 11, 11, 11],
    mem: 29,
    incidents: 0,
    state: 'Норма' as const,
  },
];

function RowMemoryDemo() {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Узел</TableHead>
          <TableHead>Состояние</TableHead>
          <TableHead className="text-right">Процессор</TableHead>
          <TableHead className="text-right">Память</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.id}>
            <TableCell className="relative font-medium">
              {r.incidents > 0 && (
                <Tooltip content={`${r.incidents} за сутки`} side="right">
                  <span
                    className="absolute top-1.5 bottom-1.5 left-0 w-1 rounded-full bg-sev-4-line"
                    aria-label={`Инцидентов за сутки: ${r.incidents}`}
                  />
                </Tooltip>
              )}
              <span className="pl-2">{r.id}</span>
            </TableCell>
            <TableCell>
              <Badge variant={r.state === 'Проблема' ? 'urgent' : 'success'} solid>
                {r.state}
              </Badge>
            </TableCell>
            <TableCell className="text-right">
              <span className="inline-flex items-center gap-2">
                <Sparkline
                  values={r.cpuTrend}
                  width={56}
                  height={16}
                  endDot={false}
                  tone={r.cpu > 60 ? 'danger' : 'muted'}
                />
                <span className="tabular-nums">{r.cpu} %</span>
              </span>
            </TableCell>
            <TableCell className="text-right tabular-nums">{r.mem} %</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function LiveNamesDemo() {
  return (
    <div className="max-w-2xl text-sm leading-6 text-fg">
      <p>
        Load average выше 8 за пять минут на{' '}
        <EntityPeek
          kind="host"
          name="host-338"
          subtitle="Гипервизор · DC-2 · Proxmox 8.2"
          state={{ label: 'Проблема', variant: 'urgent' }}
          metrics={[
            { label: 'Процессор', value: '34 %', trend: cpuTrend },
            { label: 'Память', value: '61 %', trend: memTrend },
            { label: 'Машин', value: '38' },
          ]}
          note="2 проблемы за сутки"
        />{' '}
        начиная с 03:14. Затронута{' '}
        <EntityPeek
          kind="vm"
          name="VM 100101"
          subtitle="Виртуальная машина · 4 ядра · 8 ГБ"
          state={{ label: 'Работает', variant: 'success' }}
          metrics={[
            { label: 'Процессор', value: '96 %', trend: [40, 52, 61, 70, 78, 83, 88, 91, 94, 95, 96, 96] },
            { label: 'Диск', value: '54 %' },
            { label: 'Аптайм', value: '19 сут' },
          ]}
          note="Потребляет больше всех на узле"
        />
        , по адресу{' '}
        <EntityPeek
          kind="host"
          name="192.0.2.14"
          subtitle="Адрес · подсеть 192.0.2.0/24 · DC-2"
          state={{ label: 'Отвечает', variant: 'success' }}
          metrics={[
            { label: 'Потери', value: '0 %' },
            { label: 'Задержка', value: '1.8 мс' },
            { label: 'Свободно в сети', value: '37' },
          ]}
          note="Выдан 14.08.2026"
        />
        . Дежурил{' '}
        <EntityPeek
          kind="user"
          name="Анна"
          subtitle="Дежурный · смена 00:00–08:00"
          state={{ label: 'На связи', variant: 'info' }}
          metrics={[
            { label: 'Принято', value: '7' },
            { label: 'Закрыто', value: '5' },
            { label: 'В работе', value: '2' },
          ]}
          note="Смена заканчивается через 40 мин"
        />
        .
      </p>
      <p className="mt-3 text-xs text-fg-subtle">
        Наведите на любое подчёркнутое имя: узел, машину, адрес, человека.
      </p>
    </div>
  );
}

function SafetyDemo() {
  const [left, setLeft] = useState(0);
  const running = left > 0;

  useEffect(() => {
    if (left <= 0) return;
    const id = window.setTimeout(() => setLeft((l) => l - 1), 1000);
    return () => window.clearTimeout(id);
  }, [left]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <HoldToConfirm onConfirm={() => setLeft(5)} disabled={running}>
          Перезагрузить узел
        </HoldToConfirm>
        <Button variant="secondary" size="md">
          Отмена
        </Button>
      </div>
      {running && (
        <div className="flex animate-fade-in items-center gap-3 rounded-md border border-line bg-muted px-3 py-2 text-xs">
          <span className="size-1.5 animate-live-dot rounded-full bg-warning" aria-hidden />
          <span className="text-fg">Узел отправлен в перезагрузку.</span>
          <span className="text-fg-subtle tabular-nums">осталось {left} с</span>
          <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setLeft(0)}>
            <RotateCcw className="size-3.5" />
            Отменить
          </Button>
        </div>
      )}
    </div>
  );
}

export function Character() {
  const { mode, toggle } = useTheme();
  const demos = useMemo(
    () => [
      {
        title: 'Значение показывает свою свежесть',
        idea: 'Рядом с каждой живой цифрой тонкое кольцо добирается до следующего опроса, новое значение подкатывается снизу. Остановился поток — значение выцвело и само сказало, какого оно возраста.',
        gain: 'Видно, можно ли доверять цифре, до того как принимать по ней решение. Замерший сбор данных заметен сразу, а не через полчаса.',
        node: <FreshnessDemo />,
      },
      {
        title: 'Число несёт свою историю',
        idea: 'След последних часов живёт прямо в плитке, как подложка под числом, и проявляется при наведении. Не отдельный график рядом, а свойство самого числа.',
        gain: 'Главный вопрос у живой метрики не «сколько сейчас», а «куда движется». Ответ виден без перехода в графики.',
        node: <HistoryDemo />,
      },
      {
        title: 'Строка помнит, что с ней было',
        idea: 'В списке у каждой строки свой микро-след, а слева зарубка: объект, у которого за сутки были инциденты, носит тонкую метку по тону серьёзности.',
        gain: 'Список перестаёт быть срезом на одну секунду. Беспокойные объекты видно в общей массе, даже когда прямо сейчас у них всё спокойно.',
        node: <RowMemoryDemo />,
      },
      {
        title: 'Имя остаётся живым везде',
        idea: 'Имя узла, номер машины, адрес, человек: где бы они ни встретились, они помечены пунктиром и раскрываются карточкой с состоянием сейчас.',
        gain: 'Не нужно уходить со страницы и терять место, чтобы вспомнить, что такое host-338 и как он себя чувствует.',
        node: <LiveNamesDemo />,
      },
      {
        title: 'Опасное действие с предохранителем',
        idea: 'Необратимую кнопку нужно подержать: под текстом наливается полоса. Отпустил раньше — откатилась. После срабатывания действие ещё несколько секунд можно отменить.',
        gain: 'Модалку «вы уверены?» прокликивают не глядя. Удержание нельзя сделать случайно, и оно не отвлекает от экрана.',
        node: <SafetyDemo />,
      },
    ],
    [],
  );

  return (
    <div className="h-screen overflow-y-auto bg-frame text-fg">
      <header className="sticky top-0 z-raised flex items-center gap-4 border-b border-line bg-canvas px-6 py-3">
        <Link
          to="/design"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-fg-muted hover:text-fg"
        >
          <ArrowLeft className="size-3.5" />
          Галерея
        </Link>
        <span className="text-sm font-semibold text-fg">Характер элементов</span>
        <Link to="/design/showcase" className="text-xs font-medium text-brand-fg hover:underline">
          Витрина
        </Link>
        <div className="ml-auto">
          <IconButton
            aria-label={mode === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
            variant="secondary"
            size="sm"
            onClick={toggle}
          >
            {mode === 'dark' ? <Sun /> : <Moon />}
          </IconButton>
        </div>
      </header>

      <main className="mx-auto flex max-w-5xl flex-col gap-6 px-6 py-8">
        <div className="max-w-2xl">
          <h1 className="type-title text-fg">Приёмы, из которых складывается характер</h1>
          <p className="mt-2 text-sm text-fg-muted">
            Одна идея, повторённая в каждом элементе, читается как характер системы. Здесь пять таких приёмов:
            они про то, как элементы себя ведут, а не про то, какого они цвета. Всё живое, можно трогать.
            Принятое переезжает в правила и в примитивы.
          </p>
        </div>
        {demos.map((d, i) => (
          <Demo key={d.title} n={i + 1} title={d.title} idea={d.idea} gain={d.gain}>
            {d.node}
          </Demo>
        ))}
        <PartTwo startIndex={demos.length + 1} />
      </main>
    </div>
  );
}
